import { RM_TEMPLATE_RATING_TYPE, type RmTemplateRatingType } from '@brightlocal/shared';

/** Query keys of the Reply templates requests. */
export const REPLY_TEMPLATES_QUERY_KEYS = {
  TEMPLATES: 'replyTemplates',
  AUTO_REPLY_RULES: 'replyTemplatesAutoReplyRules',
  SAVE_TEMPLATE: 'saveReplyTemplate'
} as const;

/** The dialogs Delete opens: the confirmation, or the reason the template can't be deleted. */
export const DELETE_TEMPLATE_DIALOG_KINDS = {
  CONFIRM: 'confirm',
  IN_USE: 'inUse'
} as const;

/** Templates on one page of the table. */
export const TEMPLATES_PAGE_SIZE = 10;

/** Rules per Find Auto-Reply Rules request when Delete checks every rule; the most the API returns. */
export const AUTO_REPLY_RULES_PAGE_SIZE = 100;

/** Smallest width before the templates table scrolls sideways, so the longest rating label stays whole. */
export const TEMPLATES_TABLE_MIN_WIDTH = '480px';

/** Id of the name column, the only one the table sorts by. */
export const TEMPLATE_COLUMN_ID = 'template';

/** The only `templatesSort` value the address keeps: Z to A. No value means A to Z. */
export const TEMPLATES_SORT_DESC = 'desc';

/** Place of each template rating in the form: All ratings, 5 stars down to 1, then Facebook's two. */
const TEMPLATE_RATING_POSITION = {
  [RM_TEMPLATE_RATING_TYPE.ALL_RATINGS]: 0,
  [RM_TEMPLATE_RATING_TYPE.STAR_5]: 1,
  [RM_TEMPLATE_RATING_TYPE.STAR_4]: 2,
  [RM_TEMPLATE_RATING_TYPE.STAR_3]: 3,
  [RM_TEMPLATE_RATING_TYPE.STAR_2]: 4,
  [RM_TEMPLATE_RATING_TYPE.STAR_1]: 5,
  [RM_TEMPLATE_RATING_TYPE.RECOMMENDED]: 6,
  [RM_TEMPLATE_RATING_TYPE.NOT_RECOMMENDED]: 7
} as const satisfies Record<RmTemplateRatingType, number>;

/** Every template rating, in the order the form lists them. */
export const TEMPLATE_RATING_ORDER = Object.values(RM_TEMPLATE_RATING_TYPE).sort(
  (first, second) => TEMPLATE_RATING_POSITION[first] - TEMPLATE_RATING_POSITION[second]
);

/** Label key of each template rating. */
export const TEMPLATE_RATING_LABEL_KEYS = {
  [RM_TEMPLATE_RATING_TYPE.ALL_RATINGS]: 'allRatings',
  [RM_TEMPLATE_RATING_TYPE.STAR_5]: 'star5',
  [RM_TEMPLATE_RATING_TYPE.STAR_4]: 'star4',
  [RM_TEMPLATE_RATING_TYPE.STAR_3]: 'star3',
  [RM_TEMPLATE_RATING_TYPE.STAR_2]: 'star2',
  [RM_TEMPLATE_RATING_TYPE.STAR_1]: 'star1',
  [RM_TEMPLATE_RATING_TYPE.RECOMMENDED]: 'recommended',
  [RM_TEMPLATE_RATING_TYPE.NOT_RECOMMENDED]: 'notRecommended'
} as const satisfies Record<RmTemplateRatingType, string>;

/** Field names of the template form. */
export const TEMPLATE_FORM_FIELDS = {
  NAME: 'name',
  CONTENT: 'content',
  RATING: 'reviewRatingType'
} as const;

/** Name length the API accepts. */
export const TEMPLATE_NAME_LENGTH = { MIN: 3, MAX: 30 } as const;

/** Reply length the API accepts, placeholders counted as typed. */
export const TEMPLATE_CONTENT_LENGTH = { MIN: 3, MAX: 350 } as const;

/** The only placeholders the API fills in, spelled as it expects them. */
export const TEMPLATE_PLACEHOLDERS = {
  FIRST_NAME: '{{FirstName}}',
  BUSINESS_NAME: '{{BusinessName}}'
} as const;

/** Star ratings each star threshold covers, one badge each, from 5 down. */
export const TEMPLATE_RATING_STARS: Partial<Record<RmTemplateRatingType, number[]>> = {
  [RM_TEMPLATE_RATING_TYPE.STAR_5]: [5],
  [RM_TEMPLATE_RATING_TYPE.STAR_4]: [5, 4],
  [RM_TEMPLATE_RATING_TYPE.STAR_3]: [5, 4, 3],
  [RM_TEMPLATE_RATING_TYPE.STAR_2]: [5, 4, 3, 2],
  [RM_TEMPLATE_RATING_TYPE.STAR_1]: [5, 4, 3, 2, 1]
};

/** `eventAction` a saved template sends for its rating; a star rating is the lowest star count it covers. */
export const TEMPLATE_RATING_TRACKED_VALUE = {
  [RM_TEMPLATE_RATING_TYPE.ALL_RATINGS]: 'all_ratings',
  [RM_TEMPLATE_RATING_TYPE.STAR_5]: '5',
  [RM_TEMPLATE_RATING_TYPE.STAR_4]: '4',
  [RM_TEMPLATE_RATING_TYPE.STAR_3]: '3',
  [RM_TEMPLATE_RATING_TYPE.STAR_2]: '2',
  [RM_TEMPLATE_RATING_TYPE.STAR_1]: '1',
  [RM_TEMPLATE_RATING_TYPE.RECOMMENDED]: 'recommended',
  [RM_TEMPLATE_RATING_TYPE.NOT_RECOMMENDED]: 'not_recommended'
} as const satisfies Record<RmTemplateRatingType, string>;
